import { IconButton, makeStyles } from '@material-ui/core'
import React from 'react'
import { useEffect, useRef, useState } from 'react'
import { Tree } from 'react-arborist'
import { useSelector } from 'react-redux'
import { AutoSizer } from 'react-virtualized'
import { Input, Loading } from '../../root/components/common'
import {
    isLocationSelectionDisabled,
    treeNodeChildrenAccessor,
    treeNodeIdAccessor
} from '../../root/components/common/locations-tree/tree-utils'
import Tooltip from '@material-ui/core/Tooltip'
import { FoldVerticalIcon, UnfoldVerticalIcon } from 'lucide-react'

const SINGLE_LINE_ROW_HEIGHT = 33.5
const ROW_VERTICAL_PADDING = 16

export const LocationsTree = ({
    onSearch,
    isLoading,
    renderTreeNode,
    treeUtils,
    treeRef,
    emptyText,
    currentLocation,
    isInMoveDevicesModal,
    searchStr
}) => {
    const { locale } = useSelector(state => state.main)
    const classes = useStyles({ isInMoveDevicesModal })

    const [isTreeExpanded, setIsTreeExpanded] = useState(true)

    const measuredNameHeightsRef = useRef({})
    const isRedrawScheduledRef = useRef(false)

    const getRowHeight = node =>
        Math.max(
            SINGLE_LINE_ROW_HEIGHT,
            (measuredNameHeightsRef.current[node.id] || 0) +
                ROW_VERTICAL_PADDING
        )

    // Schedule one redraw for measurements made in the same render.
    const handleNameMeasured = (id, measuredNameHeight) => {
        if (measuredNameHeightsRef.current[id] === measuredNameHeight) {
            return
        }
        measuredNameHeightsRef.current[id] = measuredNameHeight
        if (isRedrawScheduledRef.current) {
            return
        }
        isRedrawScheduledRef.current = true
        Promise.resolve().then(() => {
            isRedrawScheduledRef.current = false
            treeRef.current?.redrawList()
        })
    }

    const renderMeasuredNode = props =>
        renderTreeNode({ ...props, onMeasure: handleNameMeasured })

    // Some customer headers contain locations and must remain open.
    const openCustomerHeaders = () => {
        const tree = treeRef.current
        tree?.root.children?.forEach(node => {
            if (node.data.location?.is_customer_title) {
                tree.open(node.id)
            }
        })
    }

    const toggleAllNodes = () => {
        if (isTreeExpanded) {
            treeRef.current?.closeAll()
            openCustomerHeaders()
        } else {
            treeRef.current?.openAll()
        }
        setIsTreeExpanded(!isTreeExpanded)
    }

    useEffect(() => {
        if (searchStr) {
            treeRef.current?.openAll()
        }
    }, [searchStr, treeUtils.data, isLoading])

    useEffect(() => {
        setIsTreeExpanded(true)
    }, [searchStr, isLoading])

    const renderErrorMessage = () => {
        if (isLoading) {
            return null
        }
        if (!treeUtils.data) {
            return <div>{locale.common.errorContactSupport}</div>
        }
        if (!treeUtils.hasChildren) {
            return <div>{emptyText}</div>
        }
    }
    useEffect(() => {
        const tree = treeRef.current
        if (!currentLocation || !tree || isLoading) {
            return
        }

        let isStale = false
        const nodeId = String(currentLocation)
        tree.scrollTo(nodeId)?.then(() => {
            if (!isStale && tree.get(nodeId)) {
                tree.select(nodeId)
            }
        })

        return () => {
            isStale = true
        }
    }, [currentLocation, isLoading, treeUtils.data])
    return (
        <>
            {onSearch && (
                <div className={classes.searchInputWrapper}>
                    <div className={classes.actionWrapper}>
                        <Input
                            autoFocus
                            label={locale.common.search}
                            type="text"
                            setValue={onSearch}
                            debounce={600}
                            value={searchStr || undefined}
                            size="small"
                        />
                        <Tooltip
                            title={
                                isTreeExpanded
                                    ? locale.common.collapseTreeNodes
                                    : locale.common.expandTreeNodes
                            }>
                            <IconButton
                                className={classes.collapseButton}
                                onClick={toggleAllNodes}>
                                {isTreeExpanded ? (
                                    <FoldVerticalIcon size={20} />
                                ) : (
                                    <UnfoldVerticalIcon size={20} />
                                )}
                            </IconButton>
                        </Tooltip>
                    </div>
                </div>
            )}
            {isLoading && <Loading />}
            {renderErrorMessage()}
            {!isLoading && treeUtils.hasChildren && (
                <div className={classes.locationsWrapper}>
                    <AutoSizer>
                        {({ width, height }) => {
                            return (
                                <Tree
                                    ref={treeRef}
                                    height={height}
                                    width={width}
                                    indent={20}
                                    rowHeight={getRowHeight}
                                    rowClassName={classes.treeRow}
                                    data={treeUtils.data?.children || []}
                                    idAccessor={treeNodeIdAccessor}
                                    childrenAccessor={treeNodeChildrenAccessor}
                                    openByDefault
                                    disableSelect={isLocationSelectionDisabled}>
                                    {renderMeasuredNode}
                                </Tree>
                            )
                        }}
                    </AutoSizer>
                </div>
            )}
        </>
    )
}

const useStyles = makeStyles(theme => ({
    treeRow: {
        // Let rows shrink to the available tree width.
        minWidth: '0 !important'
    },
    locationsWrapper: ({ isInMoveDevicesModal }) => ({
        width: '100%',
        height: isInMoveDevicesModal
            ? 'calc(100% - 59px)'
            : 'calc(100% - 128px)'
    }),
    searchInputWrapper: {
        paddingRight: 5,
        marginBottom: 8
    },
    actionWrapper: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
    },
    collapseButton: {
        marginBottom: 7,
        marginLeft: 5
    }
}))
